<template>
  <div v-loading="loading">
    <div class="title">提现设置</div>

    <div v-show="!bankAccountInfo.cardNumber">
      <el-alert
        title="您在平台的收入都存在账户余额里，您可以添加提现账户后进行提现。目前仅支持银行卡账户提现，推荐使用借记卡帐户。"
        type="warning"
        show-icon
      >
      </el-alert>
      <div
        class="add-btn"
        v-if="
          authPermissions.includes(
            'expert_service.api.fuwu_admin.wallets.store'
          )
        "
      >
        <zhu-button
          text="添加银行卡"
          :shadow="true"
          type="primary"
          icon="el-icon-plus"
          @click="addVisible = true"
        >
        </zhu-button>
      </div>
    </div>

    <div v-show="bankAccountInfo.cardNumber">
      <el-card shadow="hover">
        <div slot="header">
          <span class="brand-color">提现账号</span>
        </div>
        <div class="bank-account">
          <div class="bank-card">
            <div class="bank-card-name">{{ bankAccountInfo.bankName }}</div>
            <div class="bank-card-info">姓名：{{ bankAccountInfo.name }}</div>
            <div class="bank-card-info">
              开户支行：{{ bankAccountInfo.bankBranch }}
            </div>
            <div class="bank-card-info">
              卡号：{{ bankAccountInfo.cardNumber }}
            </div>
          </div>
          <zhu-button
            v-if="
              authPermissions.includes(
                'expert_service.api.fuwu_admin.wallets.update'
              )
            "
            zhu-class="zhu-btn-plain zhu-btn-round"
            text="修改信息"
            plain
            round
            icon="el-icon-edit"
            @click="editBankAccount"
          >
          </zhu-button>
        </div>
      </el-card>
      <el-card
        shadow="hover"
        v-if="
          authPermissions.includes(
            'expert_service.api.fuwu_admin.wallets.switch_automatic_cash'
          )
        "
      >
        <div slot="header">
          <span class="brand-color">自动提现</span>
        </div>
        <div class="automatic-withdrawal">
          <div class="automatic-withdrawal-switch">
            <el-switch
              v-model="automaticWithdrawal"
              active-color="#13ce66"
              @change="toggleSwitch(automaticWithdrawal)"
            ></el-switch>
            <span
              v-show="!automaticWithdrawal"
              class="automatic-withdrawal-text info-color"
              >已关闭</span
            >
            <span
              v-show="automaticWithdrawal"
              class="automatic-withdrawal-text success-color"
              >已开启</span
            >
          </div>
          <div v-show="!automaticWithdrawal">
            您需要手动提现，系统不会自动帮您申请。请注意，如果在1号之前忘记提现，只能等到下月再提现。
          </div>
          <div v-show="automaticWithdrawal">
            每月1号23:59自动将钱包余额全部申请提现，无需再手动申请。
          </div>
        </div>
      </el-card>
      <el-card shadow="hover">
        <div slot="header">
          <span class="brand-color">认证</span>
        </div>
        <div class="tips-card">
          <div class="tips-title">
            <el-link
              class="hightlight"
              :underline="false"
              href="https://jiker.feishu.cn/file/boxcnDIDwkXTda3FoLkwvzlRY2f"
              target="_blank"
              ><i class="el-icon-question"></i> 点击查看详细操作手册</el-link
            >
          </div>
          <div class="tips-title">
            按国家规定，劳务所得按20%的税点进行个人所得税缴纳。
          </div>
          <div class="tips-item">
            为了您更好的收益，我们采用的是安全可靠的第三方渠道（税率7.4)。
          </div>
          <div class="tips-item">
            该种方式，属于个人经营所得税税种，不会干扰您的薪资综合所得税，不会被年终汇算清缴。
          </div>
        </div>
      </el-card>
    </div>

    <el-dialog
      title="添加银行卡账户"
      :visible.sync="addVisible"
      width="600px"
      :before-close="clearAddInfo"
      center
    >
      <el-form
        label-width="120px"
        :model="addInfo"
        :rules="addRules"
        ref="addInfo"
      >
        <el-form-item label="账号类型：">
          银行卡<span v-if="addInfo.cardTypeName"
            >（{{ addInfo.cardTypeName }}）</span
          >
        </el-form-item>
        <el-form-item label="卡号：" prop="cardNumber" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="addInfo.cardNumber"
            placeholder="请输入卡号"
            clearable
            @blur="getBankCardInfo(addInfo.cardNumber)"
          ></el-input>
        </el-form-item>
        <el-form-item label="开户行：" prop="bankName" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="addInfo.bankName"
            placeholder="请输入开户行"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item
          label="开户支行："
          prop="bankBranch"
          :show-message="false"
        >
          <el-input
            class="w-90pc"
            v-model="addInfo.bankBranch"
            placeholder="请输入开户支行"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="开户人姓名：" prop="name" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="addInfo.name"
            placeholder="请输入开户人姓名"
            clearable
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="clearAddInfo">取 消</el-button>
        <el-button type="primary" @click="addBankAccount" :loading="btnLoading"
          >确定</el-button
        >
      </span>
    </el-dialog>

    <el-dialog
      title="修改账户信息"
      :visible.sync="editVisible"
      width="600px"
      :before-close="clearEditInfo"
      center
    >
      <el-form
        label-width="120px"
        :model="editInfo"
        :rules="editRules"
        ref="editInfo"
      >
        <el-form-item label="账号类型：">
          银行卡<span v-if="editInfo.cardTypeName"
            >（{{ editInfo.cardTypeName }}）</span
          >
        </el-form-item>
        <el-form-item label="卡号：" prop="cardNumber" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="editInfo.cardNumber"
            placeholder="请输入卡号"
            clearable
            @blur="getBankCardInfo(editInfo.cardNumber)"
          ></el-input>
        </el-form-item>
        <el-form-item label="开户行：" prop="bankName" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="editInfo.bankName"
            placeholder="请输入开户行"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item
          label="开户支行："
          prop="bankBranch"
          :show-message="false"
        >
          <el-input
            class="w-90pc"
            v-model="editInfo.bankBranch"
            placeholder="请输入开户支行"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="开户人姓名：" prop="name" :show-message="false">
          <el-input
            class="w-90pc"
            v-model="editInfo.name"
            placeholder="请输入开户人姓名"
            clearable
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="saveBankAccount" :loading="btnLoading"
          >保存</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import bank from "@/utils/bank";
import settingService from "@/global/service/setting";

export default {
  data() {
    return {
      loading: true,
      btnLoading: false,
      bankAccountInfo: {
        id: null,
        bankName: "",
        cardTypeName: "",
        bankLogo: "",
        cardNumber: "",
        name: "",
        bankBranch: "",
        backgroundColor: ""
      },
      addVisible: false,
      addInfo: {
        cardTypeName: "",
        cardNumber: "",
        bankName: "",
        bankBranch: "",
        name: ""
      },
      addRules: {
        cardNumber: { required: true, trigger: "blur" },
        bankName: { required: true, trigger: "blur" },
        bankBranch: { required: true, trigger: "blur" },
        name: { required: true, trigger: "blur" }
      },
      editVisible: false,
      editInfo: {
        cardTypeName: "",
        cardNumber: "",
        bankName: "",
        bankBranch: "",
        name: ""
      },
      editRules: {
        cardNumber: { required: true, trigger: "blur" },
        bankName: { required: true, trigger: "blur" },
        bankBranch: { required: true, trigger: "blur" },
        name: { required: true, trigger: "blur" }
      },
      automaticWithdrawal: false
    };
  },
  computed: {
    ...mapState(["authPermissions"]),
    secretNumber() {
      return this.bankAccountInfo.cardNumber
        .split(" ")
        .join("")
        .slice(-4);
    }
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      settingService
        .withdrawalUser()
        .then(res => {
          if (res) {
            this.bankAccountInfo.id = res.id;
            this.bankAccountInfo.bankName = res.bank_name;
            this.bankAccountInfo.cardNumber = res.bank_no;
            this.bankAccountInfo.name = res.user_realname;
            this.bankAccountInfo.bankBranch = res.bank_branch;
            this.automaticWithdrawal = res.is_automatic_cash ? true : false;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getBankCardInfo(number) {
      if (bank.getBankCardInfos(number).bankName) {
        this.addInfo.cardTypeName = bank.getBankCardInfos(number).cardTypeName;
        this.editInfo.cardTypeName = bank.getBankCardInfos(number).cardTypeName;
        this.addInfo.bankName = bank.getBankCardInfos(number).bankName;
        this.editInfo.bankName = bank.getBankCardInfos(number).bankName;
      } else {
        this.addInfo.cardTypeName = "";
        this.editInfo.cardTypeName = "";
        this.addInfo.bankName = "";
        this.editInfo.bankName = "";
      }
    },
    addBankAccount() {
      this.$refs["addInfo"].validate(val => {
        if (!val) {
          this.$message.error("缺少必要参数");
          return;
        }
        if (!bank.getBankCardInfos(this.addInfo.cardNumber).bankName) {
          this.$message.error("请输入正确的银行卡信息");
          return;
        }
        const params = {
          user_realname: this.addInfo.name,
          bank_no: this.addInfo.cardNumber,
          bank_name: this.addInfo.bankName,
          bank_code: bank.getBankCardInfos(this.addInfo.cardNumber).bankCode,
          bank_branch: this.addInfo.bankBranch
        };
        this.btnLoading = true;
        settingService
          .addBankAccount(params)
          .then(() => {
            this.$message.success("添加成功");
            this.$store.dispatch("settingSatage");
            this.getData();
          })
          .finally(() => {
            this.clearAddInfo();
            this.btnLoading = false;
          });
      });
    },
    clearAddInfo() {
      this.$refs["addInfo"].resetFields();
      this.addInfo.cardTypeName = "";
      this.addVisible = false;
    },
    editBankAccount() {
      this.editVisible = true;
      this.editInfo.cardTypeName = bank.getBankCardInfos(
        this.bankAccountInfo.cardNumber
      ).cardTypeName;
      this.editInfo.cardNumber = this.bankAccountInfo.cardNumber;
      this.editInfo.bankName = this.bankAccountInfo.bankName;
      this.editInfo.bankBranch = this.bankAccountInfo.bankBranch;
      this.editInfo.name = this.bankAccountInfo.name;
    },
    saveBankAccount() {
      this.$refs["editInfo"].validate(val => {
        if (!val) {
          this.$message.error("缺少必要参数");
          return;
        }
        if (!bank.getBankCardInfos(this.editInfo.cardNumber).bankName) {
          this.$message.error("请输入正确的银行卡信息");
          return;
        }
        const id = this.bankAccountInfo.id;
        const params = {
          user_realname: this.editInfo.name,
          bank_no: this.editInfo.cardNumber,
          bank_name: this.editInfo.bankName,
          bank_code: bank.getBankCardInfos(this.editInfo.cardNumber).bankCode,
          bank_branch: this.editInfo.bankBranch
        };
        this.btnLoading = true;
        settingService
          .editBankAccount(id, params)
          .then(() => {
            this.$message.success("修改成功");
            this.getData();
          })
          .finally(() => {
            this.clearEditInfo();
            this.btnLoading = false;
          });
      });
    },
    clearEditInfo() {
      this.$refs["editInfo"].resetFields();
      this.editInfo.cardTypeName = "";
      this.editVisible = false;
    },
    toggleSwitch(value) {
      const id = this.bankAccountInfo.id;
      settingService.autoWithdrawal(id).then(() => {
        if (value) {
          this.$message.success("开启成功");
        } else {
          this.$message.success("关闭成功");
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.add-btn {
  margin: 60px 0;
  text-align: center;
}
.el-card {
  margin-bottom: 30px;
}
.bank-account {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 8px 16px 20px;
  .bank-card {
    .bank-card-name {
      margin-bottom: 24px;
      font-size: 20px;
    }
    .bank-card-info {
      margin-bottom: 12px;
      font-size: 15px;
      color: #606266;
      &:last-child {
        margin-bottom: 0;
      }
    }
    // .bank-card-number {
    //   margin-top: 44px;
    //   padding-right: 12px;
    //   width: 100%;
    //   text-align: right;
    //   font-size: 32px;
    //   .bank-card-number-star {
    //     margin-right: 8px;
    //     vertical-align: sub;
    //   }
    // }
  }
}
.tips-card {
  font-size: 14px;
  .tips-title {
    margin-bottom: 12px;
  }
  .tips-item {
    margin-bottom: 6px;
  }
  .hightlight {
    color: #409eff;
  }
}

.automatic-withdrawal {
  margin: 8px 16px 20px;
  font-size: 14px;
  color: #909399;
  .automatic-withdrawal-switch {
    display: flex;
    margin-bottom: 12px;
    .automatic-withdrawal-text {
      margin-left: 12px;
      font-size: 14px;
    }
  }
}
</style>
